import React from 'react';

import { css } from '@emotion/react';

import { useLocalizedStringFormatter, useId } from '@coursera/cds-common';

import i18nMessages from '@core/notifications/i18n';
import type {
  PageNotification,
  PageNotificationProps,
} from '@core/notifications/PageNotification';
import PageNotificationListContext from '@core/notifications/PageNotificationList/PageNotificationListContext';
import Typography from '@core/Typography2';
import VisuallyHidden from '@core/VisuallyHidden';

export type PageNotificationListProps = {
  /**
   * Unique `id` attribute for the Notification List.
   * @ignore
   */
  id?: string;
  /**
   * Array of notifications. Only supports PageNotification
   */
  children: Array<
    React.ReactElement<PageNotificationProps, typeof PageNotification>
  >;
};

/**
 * PageNotificationList component is used to stack multiple Page level notifications together.
 *
 * See [Props](__storybookUrl__/components-feedback-notifications--default#props)
 */
const PageNotificationList = (
  props: PageNotificationListProps
): React.ReactElement<PageNotificationListProps> | null => {
  const { children, id: idFromProps, ...rest } = props;
  const id = useId(idFromProps);
  const accessibilityTitleId = `${id}-a11y-title`;
  const stringFormatter = useLocalizedStringFormatter(i18nMessages);

  if (React.Children.count(children) === 0) {
    return null;
  }

  return (
    <PageNotificationListContext>
      <div id={id} {...rest}>
        <VisuallyHidden>
          <Typography
            component="h2"
            data-testid="notifications-count"
            id={accessibilityTitleId}
            variant="titleLarge"
          >
            {stringFormatter.format('page_notifications_count', {
              count: children.length,
            })}
          </Typography>
        </VisuallyHidden>
        <ul
          aria-labelledby={accessibilityTitleId}
          css={css`
            padding-inline-start: 0;
            list-style: none;
          `}
          role="list" /** This is required to for VO to annouce list on Safari. */
        >
          {React.Children.map(children, (child) => (
            <li>{child}</li>
          ))}
        </ul>
      </div>
    </PageNotificationListContext>
  );
};

export default PageNotificationList;
